import React from 'react';
import { MCQ } from '../types/mcq';
import { Button } from './ui/Button';
import { TagList } from './TagList';
import { TagSelect } from './TagSelect';
import { useTopics } from '../hooks/useTopics';
import { useSubtopics } from '../hooks/useSubtopics';

interface MCQEditProps {
  mcq: MCQ;
  onSave: (mcq: MCQ) => void;
  onCancel: () => void;
}

export function MCQEdit({ mcq, onSave, onCancel }: MCQEditProps) {
  const [editedMCQ, setEditedMCQ] = React.useState<MCQ>(mcq);
  const [selectedTag, setSelectedTag] = React.useState('');
  const { topics } = useTopics();
  const { subtopics } = useSubtopics();

  const handleRemoveTag = (tagToRemove: string) => {
    setEditedMCQ({
      ...editedMCQ,
      tags: (editedMCQ.tags || []).filter(tag => tag !== tagToRemove)
    });
  };

  const handleAddTag = () => {
    if (selectedTag && !editedMCQ.tags?.includes(selectedTag)) {
      setEditedMCQ({
        ...editedMCQ,
        tags: [...(editedMCQ.tags || []), selectedTag]
      });
      setSelectedTag('');
    }
  };

  const handleAnswerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAnswer = e.target.value;
    setEditedMCQ(prev => ({
      ...prev,
      answer: newAnswer
    }));
  };

  const handleSave = () => {
    // Ensure all required fields are present
    const updatedMCQ: MCQ = {
      ...editedMCQ,
      answer: editedMCQ.answer || 'A', // Provide default if somehow empty
      isDuplicate: editedMCQ.isDuplicate || false,
      isCompared: editedMCQ.isCompared || false,
      tags: editedMCQ.tags || [],
      similarityScore: editedMCQ.similarityScore
    };
    onSave(updatedMCQ);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
        <textarea
          value={editedMCQ.question}
          onChange={(e) => setEditedMCQ({ ...editedMCQ, question: e.target.value })}
          className="w-full p-2 border rounded-lg"
          rows={3}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Option A</label>
          <input
            value={editedMCQ.optionA}
            onChange={(e) => setEditedMCQ({ ...editedMCQ, optionA: e.target.value })}
            className="w-full p-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Option B</label>
          <input
            value={editedMCQ.optionB}
            onChange={(e) => setEditedMCQ({ ...editedMCQ, optionB: e.target.value })}
            className="w-full p-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Option C</label>
          <input
            value={editedMCQ.optionC}
            onChange={(e) => setEditedMCQ({ ...editedMCQ, optionC: e.target.value })}
            className="w-full p-2 border rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Option D</label>
          <input
            value={editedMCQ.optionD}
            onChange={(e) => setEditedMCQ({ ...editedMCQ, optionD: e.target.value })}
            className="w-full p-2 border rounded-lg"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Correct Answer</label>
        <select
          value={editedMCQ.answer}
          onChange={handleAnswerChange}
          className="w-full p-2 border rounded-lg"
        >
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="C">C</option>
          <option value="D">D</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Takeaway</label>
        <textarea
          value={editedMCQ.takeaway}
          onChange={(e) => setEditedMCQ({ ...editedMCQ, takeaway: e.target.value })}
          className="w-full p-2 border rounded-lg"
          rows={3}
          placeholder="Enter the key takeaway from this question..."
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Topic</label>
          <select
            value={editedMCQ.topic}
            onChange={(e) => setEditedMCQ({ ...editedMCQ, topic: e.target.value })}
            className="w-full p-2 border rounded-lg"
          >
            {topics.map((topic) => (
              <option key={topic.id} value={topic.value}>
                {topic.value}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Subtopic</label>
          <select
            value={editedMCQ.subtopic}
            onChange={(e) => setEditedMCQ({ ...editedMCQ, subtopic: e.target.value })}
            className="w-full p-2 border rounded-lg"
          >
            {subtopics.map((subtopic) => (
              <option key={subtopic.id} value={subtopic.value}>
                {subtopic.value}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
        <TagList 
          tags={editedMCQ.tags || []} 
          onRemove={handleRemoveTag} 
          className="mb-2"
          editable
        />
        <TagSelect
          selectedTag={selectedTag}
          onTagSelect={setSelectedTag}
          onAddTag={handleAddTag}
          disabled={false}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button
          onClick={onCancel}
          variant="outline"
          className="text-gray-600 hover:text-gray-700 border-gray-200 hover:bg-gray-50 px-6"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="bg-blue-600 text-white hover:bg-blue-700 px-6"
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
}